import React, { useRef, useState, useEffect } from 'react';
import './MultiSelectDropdown.scss';
import Dropimg from 'img/dropdown.svg';
import { ReactComponent as DropDownIcon } from 'img/dropdown.svg';

export interface Option {
  value: string | number;
  label: string;
  isSelected: boolean;
}

export interface MultiSelectDropdownProps {
  id?: string;
  placeholder?: string;
  options: Option[];
  onChange: (options: Option[]) => void;
  position?: 'top' | 'bottom';
  className?: string;
}

function onToggle(
  value: string,
  options: Option[],
  onChange: (options: Option[]) => void,
) {
  const newOptions = options.map((option) =>
    option.value === value
      ? { ...option, isSelected: !option.isSelected }
      : option,
  );
  onChange(newOptions);
}

export default function MultiSelectDropdown({
  id,
  placeholder = 'Select',
  options,
  onChange,
  position = 'bottom',
  className,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAllSelected = options.every((opt) => opt.isSelected);
  const isSomeSelected = options.some((opt) => opt.isSelected);

  const handleSelectAll = () => {
    const newOptions = options.map((opt) => ({
      ...opt,
      isSelected: !isAllSelected,
    }));
    onChange(newOptions);
  };

  const handleToggle = (value: string) => {
    onToggle(value, options, onChange);
  };

  const handleClose = () => {
    return setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true); // register capturing
    return () => window.removeEventListener('keydown', handleKeyDown, true); // unregister capturing
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedLabels = options
    .filter((o) => o.isSelected)
    .map((o) => o.label);

  const displayLabel = isAllSelected
    ? 'All'
    : selectedLabels.length > 0
      ? selectedLabels.join(', ')
      : placeholder;

  const truncatedDisplayLabel =
    displayLabel.length > 20 ? `${displayLabel.slice(0, 20)}...` : displayLabel;

  return (
    <div
      className={`multi-select-dropdown ${className ?? ''}`.trim()}
      ref={containerRef}
      id={id}
      onClick={() => setIsOpen((prev) => !prev)}
    >
      {/* <button
        className="dropdown-button"
        onClick={() => setIsOpen((prev) => !prev)}
      > */}
      <span
        className={`dropdown-placeholder ${selectedLabels.length > 0 ? 'has-value' : ''}`}
      >
        {truncatedDisplayLabel}
        {/* {selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder} */}
      </span>
      <DropDownIcon
        className={`dropdown-icon input-icon-color ${isOpen ? 'open' : ''}`}
      />
      {/* </button> */}
      {isOpen && (
        <ul className={`dropdown-options position-${position}`}>
          <li className="dropdown-option all-option" onClick={handleSelectAll}>
            <div className="dropdown-option-checkbox">
              <div className="dropdown-option-container">
                <input
                  className="dropdown-box"
                  type="checkbox"
                  readOnly
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = !isAllSelected && isSomeSelected;
                  }}
                />
                <span className="dropdown-text">All</span>
              </div>
            </div>
          </li>
          <div className="divider-line" />
          {options.map((option) => (
            <li
              key={option.value}
              className={`dropdown-option ${option.isSelected ? 'selected' : ''}`}
              onClick={() => handleToggle(option.value as string)}
            >
              <div className="dropdown-option-checkbox">
                <div className="dropdown-option-container">
                  <input
                    className="dropdown-box"
                    type="checkbox"
                    readOnly
                    checked={option.isSelected}
                  />
                  <span className="dropdown-text">{option.label}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
